import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { Callout, DefaultButton, DirectionalHint, FontIcon, IconButton, mergeStyleSets, TooltipHost } from "@fluentui/react";

export interface ActiveFilterChip {
  key: string;
  label: string;
  values: string[];
  onRemove: () => void;
}

interface ActiveFilterChipsProps {
  items: ActiveFilterChip[];
  commands?: React.ReactNode;
  commandsScrollable?: boolean;
}

const styles = mergeStyleSets({
  root: { position: "relative", display: "flex", alignItems: "center", flexWrap: "wrap", width: "100%", minWidth: 0, minHeight: "44px" },
  toolbar: { borderBottom: "1px solid var(--neutralLight, rgb(233, 222, 222))" },
  commands: { flex: "0 0 auto", maxWidth: "100%", minWidth: 0 },
  scrollableCommands: { overflowX: "auto" },
  measurement: {
    position: "absolute", top: 0, left: 0, width: "100%", display: "flex",
    visibility: "hidden", pointerEvents: "none", overflow: "hidden"
  },
  measuredChip: { flexShrink: 0 },
  chip: {
    display: "inline-flex", alignItems: "center", gap: "4px", color: "inherit",
    padding: "0 8px", height: "44px", fontSize: "14px", maxWidth: "360px",
    minWidth: 0, boxSizing: "border-box"
  },
  label: { fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" },
  value: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  count: { flexShrink: 0, whiteSpace: "nowrap" },
  icon: { fontSize: "14px", flexShrink: 0 },
  removePlaceholder: { width: "32px", height: "32px", flexShrink: 0 },
  more: {
    width: "96px", minWidth: 0, maxWidth: "100%", height: "32px", flexShrink: 0,
    boxSizing: "border-box", padding: "0 8px", borderRadius: "5px", marginLeft: 0,
    border: "1px solid var(--neutralLight, #e1e6ee)", background: "var(--neutralLighterAlt, #fafbfc)",
    color: "var(--themeDarkAlt, #365b8c)", fontSize: "13px", fontWeight: 600
  },
  moreDots: { fontSize: "21px", lineHeight: 1, marginRight: "5px", transform: "translateY(-2px)" },
  popup: { width: "374px", maxWidth: "calc(100vw - 32px)", padding: "0 12px 4px", boxSizing: "border-box" },
  popupHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0",
    fontSize: "12px", fontWeight: 600, color: "var(--neutralSecondary, #6b7480)",
    borderBottom: "1px solid var(--neutralLight, #eef0f4)"
  },
  list: { listStyle: "none", margin: 0, padding: 0 },
  row: {
    display: "flex", alignItems: "flex-start", gap: "12px", padding: "11px 0",
    selectors: { "& + &": { borderTop: "1px solid var(--neutralLight, #edebe9)" } }
  },
  fullValue: {
    flex: 1, minWidth: 0, overflowWrap: "anywhere", whiteSpace: "pre-wrap", lineHeight: "1.4",
    selectors: {
      "& strong": { display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "3px" },
      "& div": { fontSize: "12px", color: "var(--neutralSecondary, #687489)" }
    }
  }
});

/** Reserves room for the overflow button only when the full set does not fit. */
export const getVisibleActiveFilterCount = (
  widths: number[], availableWidth: number, moreWidth: number, commandsWidth?: number
): number => {
  const maxRows = commandsWidth === undefined ? 1 : 2;
  const fits = (): boolean => {
    let row = 1;
    let used = commandsWidth ?? 0;
    for (const width of widths) {
      if (used + width > availableWidth) { row += 1; used = 0; }
      if (row > maxRows || width > availableWidth) return false;
      used += width;
    }
    return true;
  };
  if (fits()) return widths.length;
  let row = 1;
  let used = commandsWidth ?? 0;
  let count = 0;
  for (const width of widths) {
    if (used + width > availableWidth) { row += 1; used = 0; }
    if (row > maxRows || width > availableWidth
      || (row === maxRows && used + width + moreWidth > availableWidth)) break;
    used += width;
    count += 1;
  }
  return count;
};

const ActiveFilterChips = ({ items, commands, commandsScrollable }: ActiveFilterChipsProps): JSX.Element | null => {
  const rootRef = useRef<HTMLDivElement>(null);
  const commandsRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLElement>(null);
  const popupId = useId();
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [isOpen, setIsOpen] = useState(false);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const measurement = measurementRef.current;
    if (!root || !measurement) {
      setIsOpen(false);
      return;
    }
    let active = true;
    const measure = (): void => {
      if (!active) return;
      const availableWidth = root.getBoundingClientRect().width;
      const widths = Array.from(measurement.children, child => Math.min(child.getBoundingClientRect().width, availableWidth));
      const moreWidth = widths.pop() ?? 0;
      const commandsWidth = commandsRef.current
        ? Math.min(commandsRef.current.getBoundingClientRect().width, availableWidth) : undefined;
      // Hidden containers have no layout yet; remeasure when they become visible.
      const count = availableWidth > 0
        ? getVisibleActiveFilterCount(widths, availableWidth, moreWidth, commandsWidth)
        : items.length;
      setVisibleCount(count);
      if (count === items.length) setIsOpen(false);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(measure);
    observer?.observe(root);
    if (commandsRef.current) observer?.observe(commandsRef.current);
    Array.from(measurement.children).forEach(child => observer?.observe(child));
    window.addEventListener("resize", measure);
    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [items, commands]);

  if (items.length === 0 && !commands) return null;

  const hidden = items.slice(visibleCount);
  const labelFor = (item: ActiveFilterChip): string => item.label.endsWith(":") ? item.label : item.label + ":";
  const fullName = (item: ActiveFilterChip): string => item.label + " " + item.values.join(", ");
  const summary = (item: ActiveFilterChip): JSX.Element => <>
    <FontIcon iconName="ClearFilter" aria-hidden={true} className={styles.icon} />
    <span className={styles.label}>{labelFor(item)}</span>
    <span className={styles.value}>{item.values[0]}</span>
    {item.values.length > 1 && <span className={styles.count} aria-hidden={true}>+{item.values.length - 1}</span>}
  </>;

  return (
    <div ref={rootRef} className={commands ? `${styles.root} ${styles.toolbar}` : styles.root}
      role="region" aria-label={commands ? "Grid controls" : "Active filters"} tabIndex={-1}>
      {commands && <div ref={commandsRef}
        className={commandsScrollable ? `${styles.commands} ${styles.scrollableCommands}` : styles.commands}
        role="group" aria-label="Grid commands">{commands}</div>}
      <div ref={measurementRef} className={styles.measurement} aria-hidden={true}>
        {items.map(item => (
          <span key={item.key} data-filter-measurement={item.key} className={`${styles.chip} ${styles.measuredChip}`}>
            {summary(item)}
            <span className={styles.removePlaceholder} />
          </span>
        ))}
        <span className={styles.more} />
      </div>
      {items.slice(0, visibleCount).map(item => (
        <TooltipHost key={item.key} content={fullName(item)}
          styles={{ root: { display: "inline-flex", flexShrink: 0, maxWidth: "100%", minWidth: 0 } }}
          tooltipProps={{ styles: { content: { overflowWrap: "anywhere" } } }}>
          <span role="group" aria-label={fullName(item)} tabIndex={0} className={styles.chip}>
            {summary(item)}
            <IconButton title="Remove Filter" ariaLabel={"Remove filter " + fullName(item)}
              styles={{ root: { flexShrink: 0 }, icon: { fontSize: "13px" } }}
              onClick={item.onRemove} iconProps={{ iconName: "ChromeClose" }} />
          </span>
        </TooltipHost>
      ))}
      {hidden.length > 0 && (
        <DefaultButton elementRef={moreRef} className={styles.more}
          ariaLabel={`Show ${hidden.length} more active filters`}
          aria-haspopup="dialog" aria-expanded={isOpen} aria-controls={isOpen ? popupId : undefined}
          onClick={() => setIsOpen(open => !open)}>
          <span className={styles.moreDots}>{"\u2026"}</span>
          <span>{` ${hidden.length} more`}</span>
        </DefaultButton>
      )}
      {isOpen && hidden.length > 0 && (
        <Callout id={popupId} target={moreRef.current} role="dialog" ariaLabel="More active filters"
          directionalHint={DirectionalHint.bottomRightEdge} isBeakVisible={false}
          setInitialFocus={true} calloutMaxHeight={320}
          styles={{ root: { borderRadius: "5px", border: "1px solid var(--neutralLight, #dde3eb)", overflow: "hidden" } }}
          onDismiss={() => setIsOpen(false)}
          onRestoreFocus={({ containsFocus }) => {
            if (containsFocus) (moreRef.current ?? rootRef.current)?.focus();
          }}>
          <div className={styles.popup}>
            <div className={styles.popupHeader}>
              <span>{hidden.length} more active {hidden.length === 1 ? "filter" : "filters"}</span>
              <IconButton ariaLabel="Close more active filters" iconProps={{ iconName: "ChromeClose" }}
                onClick={() => setIsOpen(false)} />
            </div>
            <ul className={styles.list} aria-label="Additional active filters">
              {hidden.map(item => (
                <li key={item.key} className={styles.row}>
                  <div className={styles.fullValue} role="group" aria-label={item.label}>
                    <strong>{labelFor(item)}</strong>
                    <div>{item.values.join(", ")}</div>
                  </div>
                  <IconButton ariaLabel={"Remove filter " + fullName(item)} iconProps={{ iconName: "ChromeClose" }}
                    onClick={() => { setIsOpen(false); item.onRemove(); }} />
                </li>
              ))}
            </ul>
          </div>
        </Callout>
      )}
    </div>
  );
};

export default ActiveFilterChips;
